import { Editor, Transforms, Element as SlateElement, Text, Path, Node } from 'slate';
import Link from '../Elements/Link/Link';
import Image from '../Elements/Embed/Image';
import Table from '../Elements/Table/Table';
import TableWithoutHeader from '../Elements/Table/TableWithoutHeaders';
import editorStyles from '../TextEditor.module.scss';
import tableStyles from '../Elements/Table/TableSelector.module.scss';
import TableHeader from '../Elements/Table/components/TableHeader/TableHeader';
import TableCell from '../Elements/Table/components/TableCell/TableCell';
import {
    applyFormatToMultipleSelectedColumns,
    applyFormatToMultipleSelectedRows,
    isFormatActiveInMultipleSelectedColumns,
    isFormatActiveInMultipleSelectedRows,
    isMultipleColumnsAlignmentActive,
    isMultipleRowsAlignmentActive,
    toggleMultipleColumnsAlignment,
    toggleMultipleColumnsFormat,
    toggleMultipleRowsAlignment,
    toggleMultipleRowsFormat,
} from './tableRowColumnSelections';

const alignment = ['left', 'right', 'center'];
const list_types = ['orderedList', 'unorderedList'];

export const sizeMap = {
    small: '0.75em',
    normal: '1em',
    medium: '1.75em',
    huge: '2.5em',
};
export const fontFamilyMap = {
    sans: 'Helvetica,Arial, sans serif',
    serif: 'Georgia, Times New Roaman,serif',
    monospace: 'Monaco, Courier New,monospace',
    pacifico: 'Pacifico, cursive',
};

/**
 * Handles table row/column selection formatting operations
 * @param {Editor} editor - Slate editor instance
 * @param {string} format - Format to apply
 * @param {Object} options - Callback functions for row/column handling
 * @param {Function} options.onRowsSelected - Function to call when rows are selected
 * @param {Function} options.onColumnsSelected - Function to call when columns are selected
 * @param {Array} options.alignmentFormats - Array of alignment formats to check (default: alignment array). Pass empty array to always handle.
 * @returns {boolean} True if table selection was handled, false otherwise
 */
const handleTableSelection = (editor, format, { onRowsSelected, onColumnsSelected, alignmentFormats = alignment }) => {
    // If alignmentFormats is empty, always handle. Otherwise, check if format is in the list.
    const shouldHandle = alignmentFormats.length === 0 || alignmentFormats.includes(format);

    if (editor.selectedRows?.length > 0 && shouldHandle) {
        onRowsSelected(editor, format);
        return true;
    }
    if (editor.selectedColumns?.length > 0 && shouldHandle) {
        onColumnsSelected(editor, format);
        return true;
    }
    return false;
};

/**
 * Analyzes list item position within parent list
 * @param {Path} listItemPath - Path to the list item
 * @param {Object} currentList - The parent list node
 * @returns {Object} Position info (index, isOnlyItem, isFirstItem, isLastItem)
 */
const analyzeListItemPosition = (listItemPath, currentList) => {
    const index = listItemPath[listItemPath.length - 1];
    return {
        index,
        isOnlyItem: currentList.children.length === 1,
        isFirstItem: index === 0,
        isLastItem: index === currentList.children.length - 1,
    };
};

/**
 * Converts list item to content nodes (toggle OFF scenario)
 * @param {Editor} editor - Slate editor instance
 * @param {Object} params - Parameters for conversion
 */
const convertListItemToContent = (editor, { position, contentNodes, currentListPath, listItemPath }) => {
    const { isOnlyItem, isFirstItem, isLastItem } = position;

    Editor.withoutNormalizing(editor, () => {
        if (isOnlyItem) {
            // Only item: replace entire list with content
            Transforms.removeNodes(editor, { at: currentListPath });
            Transforms.insertNodes(
                editor,
                contentNodes.map((n) => ({ ...n })),
                { at: currentListPath },
            );
            Transforms.select(editor, Editor.start(editor, currentListPath));
        } else if (isFirstItem) {
            // First item: remove it, insert content before list
            Transforms.removeNodes(editor, { at: listItemPath });
            Transforms.insertNodes(
                editor,
                contentNodes.map((n) => ({ ...n })),
                { at: currentListPath },
            );
            Transforms.select(editor, Editor.start(editor, currentListPath));
        } else if (isLastItem) {
            // Last item: remove it, insert content after list
            const insertPath = Path.next(currentListPath);
            Transforms.removeNodes(editor, { at: listItemPath });
            Transforms.insertNodes(
                editor,
                contentNodes.map((n) => ({ ...n })),
                { at: insertPath },
            );
            Transforms.select(editor, Editor.start(editor, insertPath));
        } else {
            // Middle item: split list, insert content between
            // Calculate insert path BEFORE any modifications to avoid path invalidation
            const insertPath = Path.next(currentListPath);
            const splitPath = [...currentListPath, position.index + 1];
            Transforms.splitNodes(editor, {
                at: splitPath,
                match: (n) => SlateElement.isElement(n) && list_types.includes(n.type),
            });
            Transforms.removeNodes(editor, { at: listItemPath });
            Transforms.insertNodes(
                editor,
                contentNodes.map((n) => ({ ...n })),
                { at: insertPath },
            );
            Transforms.select(editor, Editor.start(editor, insertPath));
        }
    });
};

/**
 * Changes list type (orderedList <-> unorderedList)
 * @param {Editor} editor - Slate editor instance
 * @param {Object} params - Parameters for type change
 */
const changeListType = (editor, { position, contentNodes, currentListPath, listItemPath, format }) => {
    const { isOnlyItem, isFirstItem, isLastItem } = position;

    Editor.withoutNormalizing(editor, () => {
        if (isOnlyItem) {
            // Only item: just change list type
            Transforms.setNodes(editor, { type: format }, { at: currentListPath });
        } else if (isFirstItem) {
            // First item: remove it, insert new list before
            Transforms.removeNodes(editor, { at: listItemPath });
            const newList = { type: format, children: [{ type: 'list-item', children: contentNodes.map((n) => ({ ...n })) }] };
            Transforms.insertNodes(editor, newList, { at: currentListPath });
            Transforms.select(editor, Editor.start(editor, [...currentListPath, 0, 0]));
        } else if (isLastItem) {
            // Last item: remove it, insert new list after
            const insertPath = Path.next(currentListPath);
            Transforms.removeNodes(editor, { at: listItemPath });
            const newList = { type: format, children: [{ type: 'list-item', children: contentNodes.map((n) => ({ ...n })) }] };
            Transforms.insertNodes(editor, newList, { at: insertPath });
            Transforms.select(editor, Editor.start(editor, [...insertPath, 0, 0]));
        } else {
            // Middle item: split list, insert new list between
            // Calculate insert path BEFORE any modifications to avoid path invalidation
            const insertPath = Path.next(currentListPath);
            const splitPath = [...currentListPath, position.index + 1];
            Transforms.splitNodes(editor, {
                at: splitPath,
                match: (n) => SlateElement.isElement(n) && list_types.includes(n.type),
            });
            Transforms.removeNodes(editor, { at: listItemPath });
            const newList = { type: format, children: [{ type: 'list-item', children: contentNodes.map((n) => ({ ...n })) }] };
            Transforms.insertNodes(editor, newList, { at: insertPath });
            Transforms.select(editor, Editor.start(editor, [...insertPath, 0, 0]));
        }
    });
};

/**
 * Creates new list from current block
 * @param {Editor} editor - Slate editor instance
 * @param {string} format - List type ('orderedList' or 'unorderedList')
 */
const createNewList = (editor, format) => {
    const [blockEntry] = Editor.nodes(editor, {
        match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n) && !list_types.includes(n.type) && n.type !== 'list-item',
        mode: 'lowest',
    });

    if (blockEntry) {
        const [, blockPath] = blockEntry;
        Editor.withoutNormalizing(editor, () => {
            Transforms.wrapNodes(editor, { type: 'list-item', children: [] }, { at: blockPath });
            Transforms.wrapNodes(editor, { type: format, children: [] }, { at: blockPath });
        });
    }
};

/**
 * Toggles block-level formatting (headings, lists, blockquote, alignment)
 * @param {Editor} editor - Slate editor instance
 * @param {string} format - Block format to toggle
 */
export const toggleBlock = (editor, format) => {
    // Handle table selections first
    if (
        handleTableSelection(editor, format, {
            onRowsSelected: toggleMultipleRowsAlignment,
            onColumnsSelected: toggleMultipleColumnsAlignment,
        })
    ) {
        return;
    }

    const isList = list_types.includes(format);
    const isAlignment = alignment.includes(format);

    if (isAlignment) {
        Transforms.setNodes(editor, { textAlign: format }, { match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n) });
        return;
    }

    if (isList) {
        const [currentListEntry] = Editor.nodes(editor, {
            match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && list_types.includes(n.type),
            mode: 'lowest',
        });

        const [currentListItemEntry] = Editor.nodes(editor, {
            match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'list-item',
            mode: 'lowest',
        });

        if (currentListEntry && currentListItemEntry) {
            const [currentList, currentListPath] = currentListEntry;
            const [listItem, listItemPath] = currentListItemEntry;
            const currentListType = currentList.type;
            const contentNodes = listItem.children || [];
            const position = analyzeListItemPosition(listItemPath, currentList);

            if (currentListType === format) {
                // Toggle OFF - convert list item to paragraphs
                convertListItemToContent(editor, { position, contentNodes, currentListPath, listItemPath });
            } else {
                // Change list type
                changeListType(editor, { position, contentNodes, currentListPath, listItemPath, format });
            }
        } else if (!currentListEntry) {
            // Not in a list - create new list
            createNewList(editor, format);
        }
        return;
    }

    // Non-list, non-alignment block types (headings, blockquote, etc.)
    const isActive = isBlockActive(editor, format);
    Transforms.setNodes(
        editor,
        { type: isActive ? 'paragraph' : format },
        { match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n) },
    );
};

/**
 * Adds formatting mark with value (color, fontSize, fontFamily, etc.)
 * @param {Editor} editor - Slate editor instance
 * @param {Object} data - Mark data with format and value properties
 */
export const addMarkData = (editor, data) => {
    // Handle table selections (format marks don't need alignment check)
    if (
        handleTableSelection(editor, data.format, {
            onRowsSelected: (ed, fmt) => applyFormatToMultipleSelectedRows(ed, fmt, data.value),
            onColumnsSelected: (ed, fmt) => applyFormatToMultipleSelectedColumns(ed, fmt, data.value),
            alignmentFormats: [], // Empty array means always handle if rows/columns selected
        })
    ) {
        return;
    }
    Editor.addMark(editor, data.format, data.value);
};

/**
 * Toggles text-level formatting (bold, italic, underline, etc.)
 * @param {Editor} editor - Slate editor instance
 * @param {string} format - Mark format to toggle
 */
export const toggleMark = (editor, format) => {
    // Handle table selections (format marks don't need alignment check)
    if (
        handleTableSelection(editor, format, {
            onRowsSelected: toggleMultipleRowsFormat,
            onColumnsSelected: toggleMultipleColumnsFormat,
            alignmentFormats: [], // Empty array means always handle if rows/columns selected
        })
    ) {
        return;
    }
    const isActive = isMarkActive(editor, format);

    if (isActive) {
        Editor.removeMark(editor, format);
    } else {
        Editor.addMark(editor, format, true);
    }
};

/**
 * Checks if mark format is active at current selection
 * @param {Editor} editor - Slate editor instance
 * @param {string} format - Mark format to check
 * @returns {boolean} True if format is active
 */
export const isMarkActive = (editor, format) => {
    // Check table selections first
    if (editor.selectedRows?.length > 0) {
        return isFormatActiveInMultipleSelectedRows(editor, format);
    }
    if (editor.selectedColumns?.length > 0) {
        return isFormatActiveInMultipleSelectedColumns(editor, format);
    }
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
};

/**
 * Checks if block format is active at current selection
 * @param {Editor} editor - Slate editor instance
 * @param {string} format - Block format to check
 * @returns {boolean} True if format is active
 */
export const isBlockActive = (editor, format) => {
    // Check table selections for alignment formats
    if (editor.selectedRows?.length > 0 && alignment.includes(format)) {
        return isMultipleRowsAlignmentActive(editor, format);
    }
    if (editor.selectedColumns?.length > 0 && alignment.includes(format)) {
        return isMultipleColumnsAlignmentActive(editor, format);
    }

    // For list types, check if we're inside that specific list type
    if (list_types.includes(format)) {
        const [listMatch] = Editor.nodes(editor, {
            match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
            mode: 'lowest',
        });
        return !!listMatch;
    }

    const [match] = Editor.nodes(editor, {
        match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
    });

    return !!match;
};

/**
 * Gets the active mark value for a format (color, fontSize, etc.)
 * @param {Editor} editor - Slate editor instance
 * @param {string} format - Mark format to get value for
 * @returns {*} The mark value or default value
 */
export const activeMark = (editor, format) => {
    const defaultMarkData = {
        color: 'black',
        bgColor: 'black',
        fontSize: '12px',
        fontFamily: 'sans',
        lineHeight: '130%',
    };
    const marks = Editor.marks(editor);
    const defaultValue = defaultMarkData[format];
    return marks?.[format] !== '#ffffff' ? marks?.[format] : defaultValue;
};

/**
 * Applies text formatting marks to React children
 * @param {Object} leaf - Slate leaf node with formatting marks
 * @param {ReactNode} children - React children to style
 * @returns {ReactElement} Styled children wrapped in span
 */
export const getMarked = (leaf, children) => {
    let styledChildren = children;

    // Apply all formatting in a composable way
    if (leaf.bold) {
        styledChildren = <strong>{styledChildren}</strong>;
    }

    if (leaf.code) {
        styledChildren = <code>{styledChildren}</code>;
    }

    if (leaf.italic) {
        styledChildren = <em>{styledChildren}</em>;
    }

    if (leaf.strikethrough) {
        // Use simple text-decoration for PDF compatibility
        styledChildren = (
            <s
                style={{
                    textDecoration: 'line-through',
                    textDecorationColor: leaf.color || 'currentColor',
                }}
            >
                {styledChildren}
            </s>
        );
    }

    if (leaf.underline) {
        styledChildren = (
            <u
                style={{
                    textDecoration: 'underline',
                    textDecorationColor: leaf.color || 'currentColor',
                }}
            >
                {styledChildren}
            </u>
        );
    }

    if (leaf.superscript) {
        styledChildren = <sup>{styledChildren}</sup>;
    }

    if (leaf.subscript) {
        styledChildren = <sub>{styledChildren}</sub>;
    }

    // Combine color, background, font styles into a single span to reduce nesting
    const inlineStyles = {};
    if (leaf.color) {
        inlineStyles.color = leaf.color;
    }
    if (leaf.bgColor) {
        inlineStyles.backgroundColor = leaf.bgColor;
    }
    if (leaf.fontSize) {
        inlineStyles.fontSize = leaf.fontSize;
    }
    if (leaf.fontFamily) {
        const family = fontFamilyMap[leaf.fontFamily];
        inlineStyles.fontFamily = family;
    }

    // Apply inline styles if any exist
    return (
        <span
            style={Object.keys(inlineStyles).length > 0 ? inlineStyles : {}}
            data-slate-marks={Object.keys(leaf)
                .filter((k) => k !== 'text')
                .join(',')}
        >
            {styledChildren}
        </span>
    );
};

/**
 * Renders element with appropriate React component based on element type
 * @param {Object} props - Element props including element type and children
 * @returns {ReactElement} Rendered element
 */
export const getBlock = (props) => {
    const { element, children } = props;
    const attributes = props.attributes ?? {};
    const style = { textAlign: element.textAlign };

    switch (element.type) {
        case 'headingOne':
            return (
                <h1 style={style} {...attributes} {...element.attr}>
                    {children}
                </h1>
            );
        case 'headingTwo':
            return (
                <h2 style={style} {...attributes} {...element.attr}>
                    {children}
                </h2>
            );
        case 'headingThree':
            return (
                <h3 style={style} {...attributes} {...element.attr}>
                    {children}
                </h3>
            );
        case 'blockquote':
            return (
                <blockquote style={style} {...attributes} {...element.attr}>
                    {children}
                </blockquote>
            );
        case 'list-item':
            return (
                <li style={style} {...attributes} {...element.attr}>
                    {children}
                </li>
            );
        case 'orderedList':
            return (
                <ol
                    type="1"
                    style={{
                        ...style,
                        paddingLeft: '24px',
                        listStyleType: 'decimal',
                        marginBottom: '8px',
                    }}
                    {...attributes}
                    className="dontbreak"
                >
                    {children}
                </ol>
            );
        case 'unorderedList':
            return (
                <ul
                    style={{
                        ...style,
                        paddingLeft: '24px',
                        listStyleType: 'disc',
                        marginBottom: '8px',
                    }}
                    {...attributes}
                >
                    {children}
                </ul>
            );
        case 'link':
            return <Link {...props} />;
        case 'table':
            return <Table {...props} />;
        case 'tableWithoutHeaders':
            return <TableWithoutHeader {...props} />;
        case 'table-cell':
            return <TableCell {...props} />;
        case 'table-header':
            return <TableHeader {...props} {...element.attr} style={style} />;
        case 'table-row':
            return (
                <tr style={style} {...attributes} className={tableStyles.TableRow}>
                    {children}
                </tr>
            );
        case 'image':
            return <Image {...props} />;
        // case 'htmlCode':
        //     return <HtmlCode {...props} />;
        default:
            return (
                <div style={{ ...(style || {}) }} {...element.attr} {...attributes}>
                    {children}
                </div>
            );
    }
};
